/**
 * TanStack Query hook for the public Originals catalog (Epic 20 S20.5).
 *
 * Reads the anonymous-readable `originals_stories` collection. Mirrors
 * `useReadyMadeStories` (public, no auth). The `where('isActive','==',true)`
 * constraint is MANDATORY — the anonymous Firestore rule (S20.3) evaluates the
 * list query against it; an unfiltered list is denied.
 *
 * Fetches all published stories once (sorted newest-first); age-band filtering
 * and per-band counts are done client-side in the catalog page from this set.
 *
 * @module hooks/useOriginalsStories
 */

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  type QueryDocumentSnapshot,
  type DocumentData,
} from 'firebase/firestore';
import type { AgeBand } from '@sagamoon/shared';
import { db, timestampToDate } from '@/lib/firebase';

/**
 * Lightweight catalog list item (the story body is omitted here — the full
 * doc is fetched on the story page in S20.6).
 */
export interface OriginalsStoryListItem {
  slug: string;
  title: string;
  ageBand: AgeBand;
  excerpt: string;
  readMinutes: number;
  coverImageUrl?: string;
  coverGradient?: string;
  coverEmoji?: string;
  /** Converted from the Firestore Timestamp; null if absent. */
  publishedAt: Date | null;
}

function mapOriginalsStoryDocument(
  doc: QueryDocumentSnapshot<DocumentData>
): OriginalsStoryListItem {
  const data = doc.data();
  return {
    slug: doc.id,
    title: data.title ?? '',
    ageBand: (data.ageBand ?? '3-4') as AgeBand,
    excerpt: data.excerpt ?? '',
    readMinutes: data.readMinutes ?? 0,
    coverImageUrl: data.coverImageUrl || undefined,
    coverGradient: data.coverGradient || undefined,
    coverEmoji: data.coverEmoji || undefined,
    publishedAt: timestampToDate(data.publishedAt ?? null),
  };
}

/**
 * Fetch all published Originals stories, newest first.
 * Public content — no authentication required.
 */
export function useOriginalsStories() {
  return useQuery<OriginalsStoryListItem[]>({
    queryKey: ['originals-stories'],
    queryFn: async (): Promise<OriginalsStoryListItem[]> => {
      const storiesRef = collection(db, 'originals_stories');
      const storiesQuery = query(
        storiesRef,
        where('isActive', '==', true),
        orderBy('publishedAt', 'desc')
      );
      const snapshot = await getDocs(storiesQuery);
      return snapshot.docs.map(mapOriginalsStoryDocument);
    },
    enabled: true,
    staleTime: 10 * 60 * 1000, // 10 min — catalog changes infrequently
    gcTime: 30 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}
